import React, { useState } from "react";
import { WaiterSharedProps } from "./types";
import { OrderProductStatusEnum } from "../../../enums/orders/order_status.enum";
import { useSelector } from "react-redux";
import { getCompany } from "../../../react-reduxs/slices/companies/company_slice";
import UserProfileComponent from "../../../components/UserProfileComponent";
import { toApiImage } from "../../../funcs/string-extension";

interface Props extends WaiterSharedProps {}

// Sipariş statüsü renklerini OrderProductStatusModel.color'dan alıyoruz;
// bölüm kartı rengi ise indekse göre rotate ediyoruz.
const SECTION_COLORS = [
  "#F59E0B",
  "#22C55E",
  "#3B82F6",
  "#8B5CF6",
  "#F43F5E",
  "#14B8A6",
];
const SECTION_BG = [
  "#FFFBEB",
  "#F0FDF4",
  "#EFF6FF",
  "#F5F3FF",
  "#FFF1F2",
  "#F0FDFA",
];
const SECTION_BORDER = [
  "#FDE68A",
  "#BBF7D0",
  "#BFDBFE",
  "#DDD6FE",
  "#FECDD3",
  "#99F6E4",
];

function sectionColor(idx: number) {
  return {
    dot: SECTION_COLORS[idx % SECTION_COLORS.length],
    bg: SECTION_BG[idx % SECTION_BG.length],
    border: SECTION_BORDER[idx % SECTION_BORDER.length],
  };
}

const fmt = (n: number) => n.toLocaleString("tr-TR");

const WaiterDashboard: React.FC<Props> = ({
  sections,
  allTables,
  orderStateList,
  waiterCallList,
  getOrderCount,
  navigate,
}) => {
  const [notifOpen, setNotifOpen] = useState(false);
  const company = useSelector(getCompany);
  const callTables = waiterCallList.reduce<string[]>((acc, w) => {
    if (w.tableId && !acc.includes(w.tableId)) acc.push(w.tableId);
    return acc;
  }, []);

  const totalCallCount = waiterCallList.length;

  const occupiedCount = allTables.filter((t) =>
    orderStateList.some((o) => o.order.tableId === t.id),
  ).length;

  const pendingCount = orderStateList.reduce(
    (sum, os) =>
      sum +
      os.orderProductList.filter(
        (op) => op.status.text === OrderProductStatusEnum.pendingApproval,
      ).length,
    0,
  );

  return (
    <div
      style={{
        fontFamily: "'Nunito', sans-serif",
        background: "#FAFAF9",
        minHeight: "100%",
        paddingBottom: 24,
      }}
    >
      {/* Header */}
      <div
        style={{
          background: "#1C1917",
          color: "#fff",
          padding: "12px 16px",
          display: "flex",
          alignItems: "center",
          gap: 12,
          position: "sticky",
          top: 0,
          zIndex: 50,
        }}
      >
        <div
          style={{
            width: 36,
            height: 36,
            background: "#fff",
            borderRadius: 10,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: 18,
            flexShrink: 0,
          }}
        >
          <img
            src={company?.logo.toApiImage()}
            height={36}
            width={36}
            alt="logo"
          />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontWeight: 800, fontSize: 14 }}>{company?.title}</div>
          <div
            style={{
              fontSize: 11,
              color: "#A8A29E",
              fontWeight: 600,
              marginTop: 1,
            }}
          >
            Ana Ekran · {sections.length} bölüm
          </div>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <button
            onClick={() => setNotifOpen((v) => !v)}
            style={{
              position: "relative",
              width: 36,
              height: 36,
              background: "#292524",
              border: "1px solid #44403C",
              borderRadius: 12,
              color: "#fff",
              fontSize: 15,
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            🔔
            {totalCallCount > 0 && (
              <span
                style={{
                  position: "absolute",
                  top: 7,
                  right: 7,
                  width: 8,
                  height: 8,
                  background: "#EF4444",
                  borderRadius: "50%",
                  border: "2px solid #1C1917",
                }}
              />
            )}
          </button>
          <UserProfileComponent />
        </div>
      </div>

      {/* Bildirim paneli */}
      {notifOpen && (
        <div
          style={{
            margin: "8px 16px 0",
            background: "#fff",
            borderRadius: 16,
            border: "1px solid #E7E5E4",
            overflow: "hidden",
            position: "relative",
            zIndex: 40,
          }}
        >
          <div
            style={{
              padding: "12px 16px",
              borderBottom: "1px solid #F5F5F4",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <span style={{ fontWeight: 800, fontSize: 13, color: "#1C1917" }}>
              Bekleyen Çağrılar ({totalCallCount})
            </span>
            <button
              onClick={() => setNotifOpen(false)}
              style={{
                fontSize: 11,
                fontWeight: 800,
                color: "#A8A29E",
                background: "none",
                border: "none",
                cursor: "pointer",
              }}
            >
              Kapat
            </button>
          </div>
          {callTables.length === 0 ? (
            <div
              style={{
                padding: 16,
                textAlign: "center",
                fontSize: 13,
                color: "#A8A29E",
              }}
            >
              Çağrı yok
            </div>
          ) : (
            callTables.map((tableId) => {
              const table = allTables.find((t) => t.id === tableId);
              const section = sections.find((s) => s.id === table?.sectionId);
              const calls = waiterCallList.filter((w) => w.tableId === tableId);
              return (
                <div
                  key={tableId}
                  onClick={() => {
                    setNotifOpen(false);
                    if (table)
                      navigate({
                        name: "WaiterTableDetail",
                        tableId: table.id,
                        sectionId: table.sectionId,
                      });
                  }}
                  style={{
                    padding: "12px 16px",
                    display: "flex",
                    alignItems: "center",
                    gap: 12,
                    borderBottom: "1px solid #F5F5F4",
                    cursor: "pointer",
                  }}
                >
                  <img
                    src={
                      section?.logo
                        ? ((section.logo as any).toApiImage?.() ?? section.logo)
                        : "/restaurant.png"
                    }
                    alt=""
                    style={{
                      width: 32,
                      height: 32,
                      borderRadius: 8,
                      objectFit: "cover",
                      flexShrink: 0,
                    }}
                    onError={(e) => {
                      (e.target as HTMLImageElement).src = "/restaurant.png";
                    }}
                  />
                  <div style={{ flex: 1 }}>
                    <div
                      style={{
                        fontWeight: 800,
                        fontSize: 13,
                        color: "#1C1917",
                      }}
                    >
                      {section?.name} · {table?.name || tableId}
                    </div>
                    <div
                      style={{
                        fontSize: 11,
                        color: "#A8A29E",
                        fontWeight: 600,
                      }}
                    >
                      {calls.length} çağrı
                    </div>
                  </div>
                  <span
                    style={{
                      fontSize: 11,
                      fontWeight: 800,
                      color: "#92400E",
                      background: "#FEF3C7",
                      padding: "4px 10px",
                      borderRadius: 10,
                    }}
                  >
                    ⚡ Çağrı!
                  </span>
                </div>
              );
            })
          )}
        </div>
      )}

      {/* İstatistik kartları */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr 1fr",
          gap: 10,
          padding: "14px 16px",
        }}
      >
        {[
          { val: occupiedCount, label: "Dolu Masa", color: "#1C1917" },

          {
            val: pendingCount,
            label: "Bekleyen Sipariş",
            color: pendingCount > 0 ? "#DC2626" : "#A8A29E",
          },
        ].map((s, i) => (
          <div
            key={i}
            style={{
              background: "#fff",
              borderRadius: 14,
              border: "1px solid #E7E5E4",
              padding: "12px 8px",
              textAlign: "center",
            }}
          >
            <div style={{ fontSize: 20, fontWeight: 900, color: s.color }}>
              {s.val}
            </div>
            <div
              style={{
                fontSize: 10,
                color: "#A8A29E",
                fontWeight: 700,
                marginTop: 2,
              }}
            >
              {s.label}
            </div>
          </div>
        ))}
      </div>

      {/* Bölüm kartları */}
      <div style={{ padding: "0 16px" }}>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: 10,
          }}
        >
          <span
            style={{
              fontSize: 10,
              fontWeight: 900,
              color: "#A8A29E",
              textTransform: "uppercase",
              letterSpacing: 1,
            }}
          >
            Bölümler
          </span>
          {/* <button
            onClick={() => navigate({ name: "WaiterSections" })}
            style={{
              fontSize: 12,
              fontWeight: 800,
              color: "#D97706",
              background: "none",
              border: "none",
              cursor: "pointer",
            }}
          >
            + Bölüm Ekle
          </button> */}
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {sections.map((sec, idx) => {
            const secTables = allTables.filter((t) => t.sectionId === sec.id);
            const busyCount = secTables.filter((t) =>
              orderStateList.some((o) => o.order.tableId === t.id),
            ).length;
            const callCount = waiterCallList.filter((w) =>
              secTables.some((t) => t.id === w.tableId),
            ).length;
            const readyCount = orderStateList.reduce((sum, os) => {
              const t = secTables.find((t) => t.id === os.order.tableId);
              if (!t) return sum;
              return (
                sum +
                os.orderProductList.filter(
                  (op) =>
                    op.status.text !== OrderProductStatusEnum.pendingApproval,
                ).length
              );
            }, 0);
            const revenue = orderStateList.reduce((sum, os) => {
              const t = secTables.find((t) => t.id === os.order.tableId);
              if (!t) return sum;
              return (
                sum +
                os.orderProductList.reduce(
                  (s, op) => s + op.menuProductPrice * op.amount,
                  0,
                )
              );
            }, 0);
            const c = sectionColor(idx);
            const badge = getOrderCount(sec.id);

            return (
              <button
                key={sec.id}
                onClick={() =>
                  navigate({ name: "WaiterTables", sectionId: sec.id })
                }
                style={{
                  background: "#fff",
                  borderRadius: 16,
                  border: `1px solid ${badge > 0 ? c.border : "#E7E5E4"}`,
                  padding: 14,
                  textAlign: "left",
                  cursor: "pointer",
                  width: "100%",
                  display: "flex",
                  gap: 12,
                  alignItems: "flex-start",
                  position: "relative",
                }}
              >
                {badge > 0 && (
                  <span
                    style={{
                      position: "absolute",
                      top: -6,
                      right: -6,
                      minWidth: 20,
                      height: 20,
                      background: "#EF4444",
                      borderRadius: 10,
                      color: "#fff",
                      fontSize: 11,
                      fontWeight: 900,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      padding: "0 5px",
                    }}
                  >
                    {badge}
                  </span>
                )}
                <div
                  style={{
                    width: 48,
                    height: 48,
                    borderRadius: 12,
                    background: c.bg,
                    border: `1.5px solid ${c.border}`,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                    overflow: "hidden",
                  }}
                >
                  <img
                    src={toApiImage(sec.logo, "/restaurant.png")}
                    alt={sec.name}
                    style={{
                      width: 36,
                      height: 36,
                      objectFit: "cover",
                      borderRadius: 8,
                    }}
                    onError={(e) => {
                      (e.target as HTMLImageElement).src = "/restaurant.png";
                    }}
                  />
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "flex-start",
                    }}
                  >
                    <div>
                      <div
                        style={{
                          fontWeight: 900,
                          fontSize: 14,
                          color: "#1C1917",
                        }}
                      >
                        {sec.name}
                      </div>
                      <div
                        style={{
                          fontSize: 11,
                          color: "#A8A29E",
                          fontWeight: 600,
                        }}
                      >
                        {secTables.length} masa
                      </div>
                    </div>
                    <div style={{ textAlign: "right" }}>
                      <div
                        style={{
                          fontWeight: 900,
                          fontSize: 14,
                          color: "#1C1917",
                        }}
                      >
                        ₺{fmt(revenue)}
                      </div>
                      <div
                        style={{
                          fontSize: 10,
                          color: "#A8A29E",
                          fontWeight: 600,
                        }}
                      ></div>
                    </div>
                  </div>
                  <div
                    style={{
                      display: "flex",
                      gap: 6,
                      marginTop: 10,
                      flexWrap: "wrap",
                    }}
                  >
                    <span
                      style={{
                        fontSize: 10,
                        fontWeight: 800,
                        background: "#1C1917",
                        color: "#fff",
                        padding: "4px 10px",
                        borderRadius: 10,
                      }}
                    >
                      {busyCount} dolu
                    </span>
                    {callCount > 0 && (
                      <span
                        style={{
                          fontSize: 10,
                          fontWeight: 800,
                          background: "#FEF3C7",
                          color: "#92400E",
                          padding: "4px 10px",
                          borderRadius: 10,
                        }}
                      >
                        ⚡ {callCount} çağrı
                      </span>
                    )}
                    {readyCount > 0 && (
                      <span
                        style={{
                          fontSize: 10,
                          fontWeight: 800,
                          background: "#D1FAE5",
                          color: "#065F46",
                          padding: "4px 10px",
                          borderRadius: 10,
                        }}
                      >
                        ✅ {readyCount} hazır
                      </span>
                    )}
                  </div>
                </div>
              </button>
            );
          })}
        </div>

        <button
          onClick={() => navigate({ name: "WaiterTables", sectionId: null })}
          style={{
            width: "100%",
            marginTop: 10,
            border: "2px dashed #D6D3D1",
            borderRadius: 16,
            padding: 14,
            fontSize: 13,
            fontWeight: 800,
            color: "#A8A29E",
            background: "none",
            cursor: "pointer",
          }}
        >
          Tüm Masaları Görüntüle →
        </button>
      </div>
    </div>
  );
};

export default WaiterDashboard;
